import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { VARIANT_MANAGEMENT_QUERY_KEY } from 'constants/QueryKeys.constants';
import { useApi } from 'contexts/ApiContext';

import type {
  AttributeResponse,
  VariantManagementResponse,
  VariantRequest,
  VariantResponse,
} from 'api/resources/variantManagement/types';
import { useDraft } from 'hooks/useDraft';
import useDraftMutation from 'hooks/useDraftMutation';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import { useRestApiEnabled } from 'hooks/useStreamSwitch';
import { useToast } from 'hooks/useToast';
import { generateAttributeId, generateVariantId } from 'lib/prettyUuid';

export const generateVariantManagementQuery = (
  accountId: string,
  projectId: string,
) => [accountId, projectId, VARIANT_MANAGEMENT_QUERY_KEY];

const useVariantManagement = (
  enabled: boolean = true,
  overrideData?: VariantManagementResponse | null, // pass null to override with empty data
) => {
  const { accountId, projectId } = useProjectRoute();

  const api = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isFeatureOn } = useIsFeatureOn();
  const { isProjectResourceEnabled } = useProjectResource();
  const { onProjectUpdate } = useDraft();
  const isRestApiEnabled = useRestApiEnabled();

  const isEnabled =
    enabled &&
    isFeatureOn('jupiter_variant_attributes') &&
    isProjectResourceEnabled(PROJECT_RESOURCE.VARIANT_MANAGEMENT);

  const {
    data: variantManagementData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: generateVariantManagementQuery(accountId, projectId),
    queryFn:
      overrideData === undefined
        ? () => api.methods.getVariantManagement({ accountId, projectId })
        : () => overrideData || undefined,
    enabled: isEnabled && isRestApiEnabled,
  });

  const {
    mutateAsync: putVariant,
    isLoading: isUpsertingVariant,
    isSuccess: isUpsertVariantSuccess,
  } = useDraftMutation(api.methods.putVariant, {
    onSuccess: (res, { body: { attribute_values: values } }) => {
      queryClient.setQueryData(
        generateVariantManagementQuery(accountId, projectId),
        (oldData?: VariantManagementResponse) => {
          if (!oldData) {
            return oldData;
          }

          const isDefault = res.is_default ?? false;

          const updatedVariants = oldData.variants.map(
            (variant: VariantResponse) => {
              if (variant.id === res.id) {
                return res;
              }
              if (isDefault) {
                return {
                  ...variant,
                  is_default: false,
                };
              }
              return variant;
            },
          );

          if (!updatedVariants.some(v => v.id === res.id)) {
            updatedVariants.push(res);
          }

          const updatedValues = {
            ...oldData.values,
            [res.id]: values,
          };

          return {
            ...oldData,
            variants: updatedVariants,
            values: updatedValues,
          };
        },
      );
      onProjectUpdate();
      return res;
    },
    onError: (_, { body: { variant_id: variantId } }) => {
      toast.error({
        title: `Failed to ${variantId ? 'update' : 'create'} variant`,
      });
    },
  });

  const { mutateAsync: onDeleteVariant, isLoading: isDeletingVariant } =
    useDraftMutation(api.methods.deleteVariant, {
      onSuccess: (_, { variantId }) => {
        queryClient.setQueryData(
          generateVariantManagementQuery(accountId, projectId),
          (oldData?: VariantManagementResponse) => {
            if (!oldData) {
              return oldData;
            }

            const updatedVariants = oldData.variants.filter(
              (variant: VariantResponse) => variant.id !== variantId,
            );

            const updatedValues = {
              ...oldData.values,
            };
            delete updatedValues[variantId];

            return {
              ...oldData,
              variants: updatedVariants,
              values: updatedValues,
            };
          },
        );
        onProjectUpdate();
      },
      onError: () => {
        toast.error({ title: 'Failed to delete variant' });
      },
    });

  const {
    mutateAsync: putAttribute,
    isLoading: isUpsertingAttribute,
    isSuccess: isUpsertAttributeSuccess,
  } = useDraftMutation(api.methods.putAttribute, {
    onSuccess: (res, { body: { values } }) => {
      queryClient.setQueryData(
        generateVariantManagementQuery(accountId, projectId),
        (oldData?: VariantManagementResponse) => {
          if (!oldData) {
            return oldData;
          }

          const updatedAttributes = oldData.attributes.map(
            (attribute: AttributeResponse) =>
              attribute.id === res.id ? res : attribute,
          );
          if (!updatedAttributes.some(a => a.id === res.id)) {
            updatedAttributes.push(res);
          }

          const updatedValues = Object.entries(values).reduce(
            (vals, [variantId, v]) => ({
              ...vals,
              [variantId]: { ...oldData.values[variantId], [res.id]: v },
            }),
            oldData.values,
          );

          return {
            ...oldData,
            attributes: updatedAttributes,
            values: updatedValues,
          };
        },
      );
      onProjectUpdate();
      return res;
    },
    onError: (_, { body: { attribute_id: attributeId } }) => {
      toast.error({
        title: `Failed to ${attributeId ? 'update' : 'create'} attribute`,
      });
    },
  });

  const { mutateAsync: onDeleteAttribute, isLoading: isDeletingAttribute } =
    useDraftMutation(api.methods.deleteAttribute, {
      onSuccess: (_, { attributeId }) => {
        queryClient.setQueryData(
          generateVariantManagementQuery(accountId, projectId),
          (oldData?: VariantManagementResponse) => {
            if (!oldData) {
              return oldData;
            }

            const updatedAttributes = oldData.attributes.filter(
              a => a.id !== attributeId,
            );

            const updatedValues = Object.entries(oldData.values).reduce(
              (vals, [variantId, v]) => {
                const newVals = v;
                delete v[attributeId];
                return { ...vals, [variantId]: newVals };
              },
              oldData.values,
            );

            return {
              ...oldData,
              attributes: updatedAttributes,
              values: updatedValues,
            };
          },
        );
        onProjectUpdate();
      },
      onError: () => {
        toast.error({ title: 'Failed to delete attribute' });
      },
    });

  const upsertVariant = useCallback(
    (
      variantName: string,
      newValues: { [attributeName: string]: string },
      variantId?: string,
      isDefault?: boolean,
    ) => {
      if (!variantManagementData) {
        throw new Error('No attributes available to update');
      }
      if (
        !variantManagementData.attributes.every(
          a => newValues[a.id] !== undefined && newValues[a.id] !== null,
        )
      ) {
        throw new Error('All attributes for the variant must be provided');
      }

      const id = variantId ?? generateVariantId();
      const values = Object.entries(newValues).reduce(
        (acc, [id, value]) => ({ ...acc, [id]: { value } }),
        {} as Record<string, { value: string }>,
      );

      let body: VariantRequest = {
        variant_id: id,
        name: variantName,
        attribute_values: values,
      };
      if (isDefault !== null && isDefault !== undefined) {
        body = {
          ...body,
          is_default: isDefault,
        };
      }

      return putVariant({
        accountId,
        projectId,
        body: body,
      });
    },
    [accountId, projectId, putVariant, variantManagementData],
  );

  const deleteVariant = useCallback(
    (variantId: string) => onDeleteVariant({ accountId, projectId, variantId }),
    [accountId, onDeleteVariant, projectId],
  );

  const upsertAttribute = useCallback(
    (
      attributeName: string,
      newValues: { [variantId: string]: string },
      attributeId?: string,
    ) => {
      if (!variantManagementData) {
        throw new Error('No attributes available to update');
      }
      const id = attributeId ?? generateAttributeId();

      /**
       * Handle partial updates when search is active:
       *
       * When search filtering is applied, newValues only contains values for variants
       * that match the search keyword. However, the API expects values for ALL variants.
       *
       * To support this, we merge the incoming newValues with existing values
       * to allow updating attributes without requiring all variants to be present in the Edit Panel
       */
      const allValues: { [variantId: string]: string } = {};

      if (attributeId && variantManagementData.values) {
        variantManagementData.variants.forEach(variant => {
          const existingValue =
            variantManagementData.values[variant.id]?.[attributeId]?.value;
          if (existingValue !== undefined) {
            allValues[variant.id] = existingValue;
          }
        });
      }

      Object.entries(newValues).forEach(([variantId, value]) => {
        allValues[variantId] = value;
      });

      const values = Object.entries(allValues).reduce(
        (acc, [id, value]) => ({ ...acc, [id]: { value } }),
        {} as Record<string, { value: string }>,
      );

      return putAttribute({
        accountId,
        projectId,
        body: {
          attribute_id: id,
          name: attributeName,
          values,
        },
      });
    },
    [accountId, projectId, putAttribute, variantManagementData],
  );

  const deleteAttribute = useCallback(
    (attributeId: string) =>
      onDeleteAttribute({ accountId, projectId, attributeId }),
    [accountId, onDeleteAttribute, projectId],
  );

  return {
    variantManagementData: variantManagementData,
    isVariantManagementLoading: isLoading && isEnabled,
    isVariantManagementError: isError,
    isUpsertVariantSuccess,
    isUpsertAttributeSuccess,
    upsertVariant,
    isUpsertingVariant,
    deleteVariant,
    isDeletingVariant,
    upsertAttribute,
    isUpsertingAttribute,
    deleteAttribute,
    isDeletingAttribute,
  };
};

export default useVariantManagement;
